import type { WorkspaceEntitlementsSnapshot } from '@/lib/billing/types';
import i18n from '@/lib/i18n/config';

export type BillingFeatureKey = 'ai_assistant' | 'premium_api' | 'iot_connectors' | 'automation_metering';

export type WorkspaceAccessState = 'full' | 'read_only' | 'canceled';

/**
 * Plan-aware feature gate. Undefined feature keys are treated as allowed so older rows stay compatible.
 */
export function isBillingFeatureEnabled(
  entitlements: WorkspaceEntitlementsSnapshot | null,
  feature: BillingFeatureKey
): boolean {
  if (!entitlements) return true;
  const v = entitlements.feature_entitlements[feature];
  if (v === false) return false;
  return true;
}

export function assertWithinAssetLimit(entitlements: WorkspaceEntitlementsSnapshot | null): boolean {
  if (!entitlements) return true;
  return entitlements.within_asset_limit !== false;
}

export function getAccessState(
  entitlements: WorkspaceEntitlementsSnapshot | null
): WorkspaceAccessState {
  if (!entitlements) return 'full';
  const fromTop = entitlements.access_state;
  if (fromTop === 'full' || fromTop === 'read_only' || fromTop === 'canceled') return fromTop;
  const fromFeatures = entitlements.feature_entitlements?.access_state as WorkspaceAccessState | undefined;
  if (fromFeatures === 'full' || fromFeatures === 'read_only' || fromFeatures === 'canceled') {
    return fromFeatures;
  }
  return 'full';
}

export function isTrialing(entitlements: WorkspaceEntitlementsSnapshot | null): boolean {
  if (!entitlements) return false;
  if (entitlements.is_trialing === true) return true;
  if (entitlements.feature_entitlements?.is_trialing === true) return true;
  return entitlements.subscription_status === 'trialing';
}

export function getTrialDaysRemaining(
  entitlements: WorkspaceEntitlementsSnapshot | null
): number | null {
  if (!entitlements) return null;
  const raw = entitlements.trial_days_remaining;
  if (raw == null) return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? Math.max(0, Math.ceil(n)) : null;
}

/** Throws when AI usage is blocked by access state. Callers should still check the AI feature flag separately. */
export function assertCanUseAi(entitlements: WorkspaceEntitlementsSnapshot | null): void {
  const state = getAccessState(entitlements);
  if (state !== 'full') {
    throw new Error(
      state === 'canceled'
        ? i18n.t('billing:usage.aiGate.canceled')
        : entitlements?.subscription_status === 'past_due'
          ? i18n.t('billing:usage.aiGate.pastDue')
          : i18n.t('billing:usage.aiGate.freePeriodEnded')
    );
  }
}
